import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass, Heart, CalendarDays, MessageCircle, UserRound, LayoutGrid, Car } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNotificationBadge } from "@/hooks/use-notification-badge";

/**
 * Bottom tabs for logged-in renters and agencies on mobile web.
 * Hidden on listing/checkout sticky surfaces to avoid dual chrome.
 */
export const MobileTabBar = () => {
  const { t } = useTranslation();
  const { user, getUserRole } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { unreadCount } = useNotificationBadge();
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadRole = async () => {
      if (!user) {
        setUserRole(null);
        return;
      }
      try {
        const role = await getUserRole();
        if (!cancelled) setUserRole(role);
      } catch {
        if (!cancelled) setUserRole(null);
      }
    };
    void loadRole();
    return () => {
      cancelled = true;
    };
  }, [user, getUserRole]);

  const isAgency = userRole === "owner";
  const isRenter = userRole === "renter";

  if (!isMobile || !user || (!isRenter && !isAgency)) {
    return null;
  }

  const path = location.pathname;
  const isCarDetail = /^\/cars\/[^/]+$/.test(path);
  const isCheckoutSurface =
    path.includes("/reserve") ||
    path.startsWith("/payment") ||
    path.startsWith("/verify") ||
    path.startsWith("/guest/checkout");

  if (isCarDetail || isCheckoutSurface) {
    return null;
  }

  const tabs = isAgency
    ? [
        {
          to: "/dashboard/owner",
          label: t("mobileTabBar.home"),
          icon: LayoutGrid,
          match: (p: string) => p === "/dashboard/owner",
        },
        {
          to: "/dashboard/owner/vehicles",
          label: t("mobileTabBar.vehicles"),
          icon: Car,
          match: (p: string) =>
            p.startsWith("/dashboard/owner/vehicles") ||
            p === "/cars/add" ||
            /^\/cars\/[^/]+\/(edit|availability|stats)$/.test(p),
        },
        {
          to: "/dashboard/owner/bookings",
          label: t("mobileTabBar.bookings"),
          icon: CalendarDays,
          match: (p: string) => p.startsWith("/dashboard/owner/bookings"),
        },
        {
          to: "/messages",
          label: t("mobileTabBar.messages"),
          icon: MessageCircle,
          match: (p: string) => p.startsWith("/messages"),
          badge: unreadCount,
        },
        {
          to: "/profile",
          label: t("mobileTabBar.profile"),
          icon: UserRound,
          match: (p: string) => p.startsWith("/profile"),
        },
      ]
    : [
        {
          to: "/search",
          label: t("mobileTabBar.explore"),
          icon: Compass,
          match: (p: string) => p.startsWith("/search") || p.startsWith("/cars/search"),
        },
        {
          to: "/favorites",
          label: t("mobileTabBar.favorites"),
          icon: Heart,
          match: (p: string) => p.startsWith("/favorites"),
        },
        {
          to: "/dashboard/renter/bookings",
          label: t("mobileTabBar.trips"),
          icon: CalendarDays,
          match: (p: string) =>
            p.startsWith("/dashboard/renter") ||
            p.startsWith("/bookings") ||
            p.startsWith("/guest/bookings"),
        },
        {
          to: "/messages",
          label: t("mobileTabBar.messages"),
          icon: MessageCircle,
          match: (p: string) => p.startsWith("/messages"),
          badge: unreadCount,
        },
        {
          to: "/profile",
          label: t("mobileTabBar.profile"),
          icon: UserRound,
          match: (p: string) => p.startsWith("/profile"),
        },
      ];

  return (
    <>
    <div
      className="shrink-0 md:hidden"
      style={{ height: "calc(3.5rem + env(safe-area-inset-bottom))" }}
      aria-hidden
    />
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-white/95 backdrop-blur-md md:hidden pb-[env(safe-area-inset-bottom)]"
      aria-label={t("mobileTabBar.aria")}
    >
      <ul className="grid h-14 grid-cols-5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.match(path);
          const badge = "badge" in tab ? tab.badge : 0;
          return (
            <li key={tab.to} className="min-w-0">
              <NavLink
                to={tab.to}
                className={cn(
                  "relative flex h-full flex-col items-center justify-center gap-0.5 px-1 text-[10px] font-medium transition-colors",
                  active ? "text-green-night" : "text-text-tertiary hover:text-text-primary"
                )}
                aria-current={active ? "page" : undefined}
              >
                <span className="relative">
                  <Icon className={cn("h-5 w-5", active && "stroke-[2.25]")} aria-hidden />
                  {typeof badge === "number" && badge > 0 && (
                    <span className="absolute -top-1.5 -end-2 min-w-[14px] h-3.5 rounded-full bg-green-night px-1 text-center text-[9px] font-bold leading-[14px] text-white">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>
                <span className="max-w-full truncate">{tab.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
    </>
  );
};

export default MobileTabBar;
